import { useAdminData } from '../context/AdminDataContext';
import {
  Building2, MessageSquare, Star, MousePointerClick,
  TrendingUp, Plus, Eye, ArrowRight, Clock, Folder,
  CheckCircle, AlertCircle, Activity, FileText
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

function StatCard({ icon: Icon, label, value, sub, color, iconBg, link }) {
  return (
    <Link to={link || '#'} className="block group">
      <div className="bg-white border border-gray-200 rounded-2xl p-6 hover:border-[#47ace2]/40 hover:shadow-lg hover:shadow-[#47ace2]/8 transition-all duration-300">
        <div className="flex items-start justify-between mb-4">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${iconBg}`}>
            <Icon className={`w-6 h-6 ${color}`} />
          </div>
          <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-[#47ace2] group-hover:translate-x-1 transition-all" />
        </div>
        <div className="text-3xl font-bold text-slate-800 font-outfit mb-1">{value}</div>
        <div className="text-slate-500 font-manrope text-sm font-medium">{label}</div>
        {sub && <div className="text-slate-400 font-manrope text-xs mt-1">{sub}</div>}
      </div>
    </Link>
  );
}

function ActivityItem({ item }) {
  const icons = {
    enquiry: MessageSquare,
    project: Building2,
    testimonial: Star,
    cta: MousePointerClick,
    content: FileText,
  };
  const colors = {
    enquiry: 'text-blue-500 bg-blue-50',
    project: 'text-[#47ace2] bg-[#47ace2]/10',
    testimonial: 'text-amber-500 bg-amber-50',
    cta: 'text-purple-500 bg-purple-50',
    content: 'text-green-500 bg-green-50',
  };
  const Icon = icons[item.type] || Activity;
  const colorClass = colors[item.type] || 'text-slate-400 bg-slate-50';
  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  };
  return (
    <div className="flex items-start gap-3 py-3">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${colorClass}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-slate-600 text-sm font-manrope leading-snug">{item.message}</p>
        <p className="text-slate-400 text-xs font-manrope mt-0.5 flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {timeAgo(item.time || item.created_at)}
        </p>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { stats, enquiries, ctaEvents, activities, refreshDashboard } = useAdminData();

  const projectStatusData = [
    { name: 'Ongoing', value: stats.ongoingProjects, color: '#47ace2' },
    { name: 'Completed', value: stats.completedProjects, color: '#22c55e' },
    { name: 'Upcoming', value: stats.upcomingProjects, color: '#f59e0b' },
  ];

  const ctaByAction = (ctaEvents || []).reduce((acc, e) => {
    const key = e.action;
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const ctaChartData = Object.entries(ctaByAction).map(([name, count]) => ({ name, count }));

  const enquiryStatusData = [
    { name: 'New', value: stats.newEnquiries || enquiries.filter(e => e.status === 'New').length },
    { name: 'Contacted', value: enquiries.filter(e => e.status === 'Contacted').length },
    { name: 'In Progress', value: enquiries.filter(e => e.status === 'In Progress').length },
    { name: 'Closed', value: enquiries.filter(e => e.status === 'Closed').length },
  ];

  const statCards = [
    { icon: Building2, label: 'Total Projects', value: stats.totalProjects, sub: `${stats.ongoingProjects} ongoing`, iconBg: 'bg-[#47ace2]/10', color: 'text-[#47ace2]', link: '/admin/projects' },
    { icon: MessageSquare, label: 'Total Enquiries', value: stats.totalEnquiries, sub: `${stats.newEnquiries} new`, iconBg: 'bg-blue-50', color: 'text-blue-500', link: '/admin/enquiries' },
    { icon: Star, label: 'Testimonials', value: stats.totalTestimonials, sub: 'Active reviews', iconBg: 'bg-amber-50', color: 'text-amber-500', link: '/admin/testimonials' },
    { icon: MousePointerClick, label: 'CTA Clicks', value: stats.ctaClicks, sub: 'All-time tracked', iconBg: 'bg-purple-50', color: 'text-purple-500', link: '/admin/cta' },
  ];

  return (
    <div className="p-6 md:p-8 space-y-8 animate-fade-in">
      {/* Page Title */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 font-outfit">Dashboard</h1>
          <p className="text-slate-400 font-manrope text-sm mt-1">Welcome back! Here's what's happening.</p>
        </div>
        <Link
          to="/admin/projects"
          className="flex items-center gap-2 px-4 py-2 bg-[#47ace2] hover:bg-[#1f7db3] text-white rounded-xl font-manrope text-sm font-medium transition-colors shadow-md shadow-[#47ace2]/25"
        >
          <Plus className="w-4 h-4" />
          Add Project
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
        {statCards.map(card => <StatCard key={card.label} {...card} />)}
      </div>

      {/* Sub stats row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        {[
          { icon: TrendingUp, value: stats.ongoingProjects, label: 'Ongoing Projects', bg: 'bg-[#47ace2]/10', color: 'text-[#47ace2]' },
          { icon: CheckCircle, value: stats.completedProjects, label: 'Completed Projects', bg: 'bg-green-50', color: 'text-green-500' },
          { icon: AlertCircle, value: stats.upcomingProjects, label: 'Upcoming Projects', bg: 'bg-amber-50', color: 'text-amber-500' },
        ].map(item => (
          <div key={item.label} className="bg-white border border-gray-200 rounded-2xl p-5 flex items-center gap-4 shadow-sm">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${item.bg}`}>
              <item.icon className={`w-5 h-5 ${item.color}`} />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-800 font-outfit">{item.value}</div>
              <div className="text-slate-400 text-sm font-manrope">{item.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* CTA Chart */}
        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-slate-700 font-semibold font-outfit">CTA Activity</h2>
            <Link to="/admin/cta" className="text-[#47ace2] text-xs font-manrope hover:underline flex items-center gap-1">
              View All <Eye className="w-3 h-3" />
            </Link>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={ctaChartData} barSize={28}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 11, fontFamily: 'Manrope' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#94a3b8', fontSize: 11, fontFamily: 'Manrope' }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', color: '#1e293b', boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}
                cursor={{ fill: '#47ace208' }}
              />
              <Bar dataKey="count" fill="#47ace2" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Project Status Donut */}
        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <h2 className="text-slate-700 font-semibold font-outfit mb-5">Project Status</h2>
          <ResponsiveContainer width="100%" height={150}>
            <PieChart>
              <Pie data={projectStatusData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={3} dataKey="value">
                {projectStatusData.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', color: '#1e293b' }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-2 mt-4">
            {projectStatusData.map(item => (
              <div key={item.name} className="flex items-center justify-between text-xs font-manrope">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: item.color }} />
                  <span className="text-slate-500">{item.name}</span>
                </div>
                <span className="text-slate-700 font-semibold">{item.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-slate-700 font-semibold font-outfit">Recent Activity</h2>
            <span className="text-xs text-slate-400 font-manrope">Last 20 actions</span>
          </div>
          <div className="divide-y divide-gray-100">
            {activities.slice(0, 6).map(item => (
              <ActivityItem key={item.id} item={item} />
            ))}
          </div>
        </div>

        {/* Quick Actions + Enquiry Status */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
            <h2 className="text-slate-700 font-semibold font-outfit mb-4">Quick Actions</h2>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Add Project', icon: Building2, link: '/admin/projects', color: 'bg-[#47ace2]/10 hover:bg-[#47ace2]/20 text-[#47ace2]' },
                { label: 'Add Testimonial', icon: Star, link: '/admin/testimonials', color: 'bg-amber-50 hover:bg-amber-100 text-amber-500' },
                { label: 'View Enquiries', icon: MessageSquare, link: '/admin/enquiries', color: 'bg-blue-50 hover:bg-blue-100 text-blue-500' },
                { label: 'Edit Content', icon: Folder, link: '/admin/content', color: 'bg-green-50 hover:bg-green-100 text-green-500' },
              ].map(action => (
                <Link
                  key={action.label}
                  to={action.link}
                  className={`flex flex-col items-center gap-2 p-4 rounded-xl transition-all font-manrope text-sm font-medium ${action.color} border border-transparent hover:border-current/10`}
                >
                  <action.icon className="w-5 h-5" />
                  {action.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Enquiry Status */}
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-slate-700 font-semibold font-outfit">Enquiry Status</h2>
              <Link to="/admin/enquiries" className="text-[#47ace2] text-xs font-manrope hover:underline">View All</Link>
            </div>
            <div className="space-y-3">
              {enquiryStatusData.map(item => (
                <div key={item.name} className="flex items-center justify-between">
                  <span className="text-slate-500 text-sm font-manrope">{item.name}</span>
                  <div className="flex items-center gap-3">
                    <div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#47ace2] rounded-full"
                        style={{ width: `${stats.totalEnquiries ? (item.value / stats.totalEnquiries) * 100 : 0}%` }}
                      />
                    </div>
                    <span className="text-slate-700 text-sm font-manrope font-semibold w-4 text-right">{item.value}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
